import { useEffect, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useAuth } from 'contexts/AuthProvider';
import { ORDER_STAGE_TYPES, VISIBILITY_INDEX_TYPE } from 'helpers/enums';


const VISIBILITY_PATHS = {
  [VISIBILITY_INDEX_TYPE.ORDER_VIEW]: '/order/item',
  [VISIBILITY_INDEX_TYPE.SPECIAL_ORDER_VIEW]: '/order/special-item',
  [VISIBILITY_INDEX_TYPE.SPECIAL_VIEW]: '/order/special',
  [VISIBILITY_INDEX_TYPE.HALF_HALF_VIEW]: '/order/half-half',
  [VISIBILITY_INDEX_TYPE.HALF_ORDER_VIEW]: '/order/half-item',
  [VISIBILITY_INDEX_TYPE.VOUCHER_VIEW]: '/order/voucher',
  [VISIBILITY_INDEX_TYPE.VOUCHER_ORDER_VIEW]: '/order/voucher-item',
};

const AUTH_PATHS = {
  [ORDER_STAGE_TYPES.OAUTH_FORM]: '/order/auth',
  [ORDER_STAGE_TYPES.NAME_FORM]: '/order/auth/name',
  [ORDER_STAGE_TYPES.MOBILE_FORM]: '/order/auth/mobile',
  [ORDER_STAGE_TYPES.VERIFY_FROM]: '/order/auth/verify',
};

const STAGE_PATHS = {
  [ORDER_STAGE_TYPES.REVIEW]: '/order/review',
  [ORDER_STAGE_TYPES.PAYMENT]: '/order/payment',
  [ORDER_STAGE_TYPES.PAYMENT_CARD]: '/order/payment',
};

function pathToView(pathname) {
  return pathname === '/' ? 'home' : pathname.replace(/^\//, '');
}

function emitSidebarAnalytics(view) {
  if (typeof window === 'undefined') return;

  const params = { view, page_location: window.location.href };

  if (typeof window.gtag === 'function') {
    window.gtag('event', 'sidebar_view', {
      ...params,
      page_title: `sidebar:${view}`,
    });
  }

  // Meta Pixel
  if (typeof window.fbq === 'function') {
    window.fbq('trackCustom', 'SidebarView', params);
  }
}

function isManagedPath(pathname) {
  if (pathname.includes('dine-in')) return false;
  return (
    pathname === '/' ||
    pathname === '/order' ||
    pathname.startsWith('/order/') ||
    pathname === '/booking' ||
    pathname === '/about' ||
    pathname === '/loyalty' ||
    pathname === '/profile'
  );
}

function getSidebarPath({
  bookingOpen,
  aboutOpen,
  loyaltyOpen,
  profileOpen,
  visibilityIndex,
  authOrderStage,
  stage,
  hasItems,
}) {
  // Tiers 1-4: full-sidebar overlays
  if (bookingOpen) return '/booking';
  if (aboutOpen) return '/about';
  if (loyaltyOpen) return '/loyalty';
  if (profileOpen) return '/profile';

  // Tier 6: item configurator panels take priority over auth/stage
  if (visibilityIndex && VISIBILITY_PATHS[visibilityIndex]) {
    return VISIBILITY_PATHS[visibilityIndex];
  }

  // Tier 7: auth sub-flow
  if (AUTH_PATHS[authOrderStage]) {
    return AUTH_PATHS[authOrderStage];
  }

  // Order complete — sidebar is hidden, treat as home
  if (stage >= ORDER_STAGE_TYPES.ALL_DONE) {
    return '/';
  }

  // Tier 8: checkout stage
  if (STAGE_PATHS[stage]) {
    return STAGE_PATHS[stage];
  }

  // Tier 5: default cart
  return hasItems ? '/order' : '/';
}

export default function useSidebarUrlReflection({ rightSideViewVisibilityIndex }) {
  const history = useHistory();
  const location = useLocation();
  const { bookingOpen, aboutOpen, loyaltyOpen, profileOpen, currentOrder } =
    useSelector((state) => state.homeReducer);
  const { authOrderStage } = useAuth();

  const lastEmittedView = useRef(null);

  const stage = currentOrder?.stage ?? ORDER_STAGE_TYPES.NONE;
  const hasItems = (currentOrder?.menuItems?.length ?? 0) > 0;

  useEffect(() => {
    const targetPath = getSidebarPath({
      bookingOpen,
      aboutOpen,
      loyaltyOpen,
      profileOpen,
      visibilityIndex: rightSideViewVisibilityIndex,
      authOrderStage,
      stage,
      hasItems,
    });
    const view = pathToView(targetPath);

    // Fire analytics once per distinct view. This runs regardless of whether we
    // rewrite the URL, so views are still tracked while on non-managed paths
    // (e.g. /category/*) where we intentionally leave the URL alone.
    if (view !== lastEmittedView.current) {
      lastEmittedView.current = view;
      emitSidebarAnalytics(view);
    }

    // URL reflection: only overwrite paths we own, and only when it changed.
    if (isManagedPath(location.pathname) && targetPath !== location.pathname) {
      history.replace({
        pathname: targetPath,
        search: location.search,
        hash: location.hash,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    bookingOpen,
    aboutOpen,
    loyaltyOpen,
    profileOpen,
    rightSideViewVisibilityIndex,
    authOrderStage,
    stage,
    hasItems,
    location.pathname,
  ]);
}
